/**
 * Datenhaltung im Client: Server-Stand im IndexedDB-Cache, Änderungen über
 * die Outbox, Synchronisation bei Verbindung.
 */
import { api, ApiError, syncEntries } from './api.js';
import { kvClear, kvGet, kvSet, idbOutboxStorage } from './idb.js';
import { ActionQueue, type ActionReport, type ActionResult, type QueuedAction } from './actions.js';
import { newId } from './clock.js';
import { mergeLocal, NetworkError, Outbox, type FlushReport, type LocalEntry } from './outbox.js';
import type { EntryChange, EntryDTO, UserDTO } from '../shared/types.js';
import { monthOf } from '../shared/time.js';

export interface Me {
  user: UserDTO;
  activities: string[];
  running: EntryDTO | null;
  serverTime: number;
}

export const outbox = new Outbox(idbOutboxStorage);
export const actionQueue = new ActionQueue({
  async load() {
    return (await kvGet<QueuedAction[]>('actions')) ?? [];
  },
  async save(items) {
    await kvSet('actions', items);
  },
});
let me: Me | null = null;
let lastReport: FlushReport | null = null;

export function currentMe(): Me | null {
  return me;
}

export function isAdmin(): boolean {
  return me?.user.role === 'admin';
}

/** Lädt den angemeldeten Benutzer; offline aus dem Cache. null = nicht angemeldet. */
export async function loadMe(): Promise<Me | null> {
  try {
    me = await api<Me>('/me', { fresh: true });
    const previous = await kvGet<Me>('me');
    if (previous && previous.user.id !== me.user.id) {
      // Anderer Benutzer auf diesem Gerät: lokale Daten des vorherigen nie unter
      // der neuen Sitzung hochladen.
      await kvClear();
      await outbox.reset();
      await actionQueue.reset();
    }
    await kvSet('me', me);
    // Stempeluhr auf einem anderen Gerät gestartet? Übernehmen.
    const clock = await getClock();
    if (!clock && me.running) await kvSet('clock', toChange(me.running));
    if (clock && me.running === null) {
      // Lokal läuft etwas, das der Server (noch) nicht kennt – nur behalten, wenn es in der Outbox wartet.
      const waiting = (await outbox.list()).some((i) => i.id === clock.id);
      if (!waiting) await kvSet('clock', null);
    }
    return me;
  } catch (err) {
    if (err instanceof NetworkError) {
      me = (await kvGet<Me>('me')) ?? null;
      return me;
    }
    if (err instanceof ApiError && err.status === 401) {
      me = null;
      return null;
    }
    throw err;
  }
}

export async function logout(): Promise<void> {
  if ((await outbox.pendingCount()) + (await actionQueue.count()) > 0 && !confirm('Es warten noch Einträge auf den Upload. Trotzdem abmelden? Diese gehen dann verloren.')) {
    return;
  }
  try {
    await api('/auth/logout', { method: 'POST' });
  } catch {
    /* offline: lokal trotzdem abmelden */
  }
  await kvClear();
  // Personenbezogene Daten auch aus dem Service-Worker-Cache entfernen.
  navigator.serviceWorker?.controller?.postMessage('clear-api-cache');
  localStorage.removeItem('ze:lastActivity');
  me = null;
  location.hash = '#/login';
  location.reload();
}

export function toChange(e: EntryDTO): EntryChange {
  const { late: _late, ...rest } = e;
  return rest;
}

const monthKey = (m: string) => `entries:${me?.user.id}:${m}`;

export async function cachedMonth(month: string): Promise<{ entries: EntryDTO[]; locked: boolean }> {
  return (await kvGet<{ entries: EntryDTO[]; locked: boolean }>(monthKey(month))) ?? { entries: [], locked: false };
}

/** Holt einen Monat vom Server und legt ihn im Cache ab. */
export async function refreshMonth(month: string): Promise<{ entries: EntryDTO[]; locked: boolean } | null> {
  try {
    const r = await api<{ entries: EntryDTO[]; locked: boolean }>(`/entries?month=${month}`, { fresh: true });
    // Läuft parallel ein Sync, kann die Antwort älter sein als das, was der Sync
    // bereits in den Cache geschrieben hat – den neueren Stand behalten.
    const cached = new Map((await cachedMonth(month)).entries.map((e) => [e.id, e]));
    const entries = r.entries.map((e) => {
      const c = cached.get(e.id);
      return c && c.updatedAt > e.updatedAt ? c : e;
    });
    const data = { entries, locked: r.locked };
    await kvSet(monthKey(month), data);
    return data;
  } catch (err) {
    if (err instanceof NetworkError) return null;
    throw err;
  }
}

export async function localMonth(month: string): Promise<{ entries: LocalEntry[]; locked: boolean }> {
  const c = await cachedMonth(month);
  const entries = mergeLocal(c.entries, await outbox.list(), me?.user.id ?? 0, (e) => monthOf(e.date) === month);
  return { entries, locked: c.locked };
}

/** Einträge aller gecachten Monate um `date` herum (für lokale Überlappungsprüfung). */
export async function localAround(date: string): Promise<LocalEntry[]> {
  const months = new Set([monthOf(date)]);
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() - 1);
  months.add(d.toISOString().slice(0, 7));
  d.setUTCDate(d.getUTCDate() + 2);
  months.add(d.toISOString().slice(0, 7));
  const out: LocalEntry[] = [];
  for (const m of months) out.push(...(await localMonth(m)).entries);
  return out;
}

async function putIntoCache(e: EntryDTO): Promise<void> {
  const m = monthOf(e.date);
  const c = await cachedMonth(m);
  const idx = c.entries.findIndex((x) => x.id === e.id);
  if (idx >= 0) c.entries[idx] = e;
  else c.entries.push(e);
  // Falls der Eintrag den Monat gewechselt hat: aus anderen Monaten entfernen erledigt refreshMonth.
  await kvSet(monthKey(m), c);
}

export async function saveChange(change: EntryChange): Promise<void> {
  await outbox.enqueue({ ...change, userId: change.userId ?? me?.user.id });
  void syncNow();
}

export function lastSyncReport(): FlushReport | null {
  return lastReport;
}

/** Synchronisiert die Outbox und aktualisiert den Cache mit der Serverantwort. */
export async function syncNow(): Promise<FlushReport | null> {
  if (!me) return null;
  const report = await outbox.flush(syncEntries);
  lastReport = report;
  const clock = await getClock();
  for (const e of [...report.ok, ...report.conflicts]) {
    await putIntoCache(e);
    if (clock && clock.id === e.id && (e.end !== null || e.deleted)) await kvSet('clock', null);
  }
  for (const r of report.rejected) {
    if (clock && clock.id === r.id && !r.entry) {
      // Start der Stempeluhr abgelehnt (z. B. Überschneidung): Uhr lokal stoppen, Fehler bleibt sichtbar.
      await kvSet('clock', null);
    }
  }
  window.dispatchEvent(new CustomEvent('ze:synced', { detail: report }));
  if (!report.offline) await flushActions();
  return report;
}

/** Aktion einreihen und – wenn möglich – sofort senden. Liefert die Aktions-ID. */
export async function enqueueAction(type: string, payload: Record<string, unknown>, id = newId()): Promise<string> {
  await actionQueue.enqueue({ id, type, payload, at: Date.now() });
  void flushActions();
  return id;
}

export async function flushActions(): Promise<ActionReport | null> {
  if (!me) return null;
  const report = await actionQueue.flush(async (actions) => {
    const r = await api<{ results: ActionResult[] }>('/actions', { method: 'POST', body: { actions } });
    return r.results;
  });
  window.dispatchEvent(new CustomEvent('ze:actions', { detail: report }));
  return report;
}

export async function getClock(): Promise<EntryChange | null> {
  return (await kvGet<EntryChange | null>('clock')) ?? null;
}

export async function setClock(e: EntryChange | null): Promise<void> {
  if (!e) return kvSet('clock', null);
  // Stempeluhr-Schritte laufen ohne Versionsbasis (last-write-wins), sonst würde
  // eine im Formular gesetzte Basis spätere Pause/Gehen-Schritte als Konflikt verwerfen.
  const { baseUpdatedAt: _base, ...clock } = e;
  await kvSet('clock', clock);
}

export async function cachedGet<T>(key: string, path: string): Promise<{ data: T | null; offline: boolean }> {
  try {
    const data = await api<T>(path, { fresh: true });
    await kvSet(key, data);
    return { data, offline: false };
  } catch (err) {
    if (err instanceof NetworkError) return { data: (await kvGet<T>(key)) ?? null, offline: true };
    throw err;
  }
}
